import type { IconProps } from "@/components/ui/icons/icon-base"

const frame = (width: number, height: number, props: IconProps) => ({
  width,
  height,
  viewBox: `0 0 ${width} ${height}`,
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  ...props,
})

interface NavGlyphProps extends Omit<IconProps, "size"> {
  filled?: boolean
}

const fillWhen = (filled: boolean) => (filled ? "currentColor" : "none")

export const NavHomeIcon = ({ filled = false, ...props }: NavGlyphProps) => (
  <svg {...frame(23, 23, props)}>
    <path
      fill={fillWhen(filled)}
      d="M22.25 21.3438C22.2449 21.5767 22.1516 21.8049 21.9814 21.9766C21.8082 22.1513 21.5736 22.2489 21.3301 22.25H15.8545C15.4403 22.25 15.1045 21.9142 15.1045 21.5V17.2012C15.1045 16.2321 14.7226 15.3034 14.0459 14.6201C13.3694 13.9372 12.4534 13.5547 11.5 13.5547C10.5466 13.5547 9.63061 13.9372 8.9541 14.6201C8.27736 15.3034 7.89551 16.2321 7.89551 17.2012V21.4971C7.89546 21.9112 7.55969 22.2471 7.14551 22.2471H1.66602C1.41965 22.2452 1.18719 22.1449 1.01758 21.9736C0.935516 21.8908 0.868249 21.7906 0.821289 21.6777C0.802691 21.633 0.787961 21.5865 0.77832 21.5391L0.764648 21.4727C0.754716 21.4236 0.75 21.3733 0.75 21.3232V9.25879C0.750008 9.23771 0.751146 9.21632 0.75293 9.19531C0.774789 8.93876 0.884496 8.69819 1.06348 8.51367L1.14746 8.43848L11.0488 0.90332L11.1543 0.835938C11.4088 0.702164 11.7224 0.724745 11.957 0.90332L21.8525 8.44043C21.8836 8.46411 21.9134 8.49032 21.9404 8.51855C22.1133 8.69923 22.225 8.93687 22.2471 9.19727C22.2489 9.21833 22.25 9.2396 22.25 9.26074V21.3438Z"
    />
  </svg>
)

const GRID_CELLS = [0.75, 12.75] as const

export const NavGridIcon = ({ filled = false, ...props }: NavGlyphProps) => (
  <svg {...frame(20, 20, props)}>
    {GRID_CELLS.flatMap((y) => GRID_CELLS.map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="6.5" height="6.5" rx="0.25" fill={fillWhen(filled)} />))}
  </svg>
)

export const NavBagIcon = ({ filled = false, ...props }: NavGlyphProps) => (
  <svg {...frame(23.0001, 23.0009, props)} strokeLinecap="butt" strokeLinejoin="miter">
    <path
      fill={fillWhen(filled)}
      d="M11.791 0.755859C14.5766 0.87804 16.9148 2.87143 16.9151 5.5957V6.27441H18.9063C19.9718 6.27441 20.7294 6.99506 20.8194 7.79297L22.0606 18.8125L22.2403 20.4014C22.2884 20.8285 22.1561 21.2533 21.8418 21.6045V21.6055C21.4835 22.0054 20.9363 22.2509 20.3282 22.251H2.67093C2.13577 22.2509 1.65041 22.0617 1.29788 21.7461L1.28226 21.7324L1.26761 21.7178L1.1846 21.6338L1.168 21.6172L1.15238 21.6006C0.840189 21.2471 0.710788 20.8214 0.760775 20.3936V20.3926L2.25785 7.7832C2.35199 6.99053 3.111 6.2745 4.16898 6.27441H6.12406V5.5957C6.12435 2.91354 8.63008 0.750105 11.5196 0.75L11.791 0.755859Z"
    />
    {!filled && <path d="M6.73791 6.2739H16.2621" />}
  </svg>
)

export const NavUserIcon = ({ filled = false, ...props }: NavGlyphProps) => (
  <svg {...frame(18.7331, 22, props)}>
    <path
      fill={fillWhen(filled)}
      d="M9.57033 0.754883C11.6613 0.86088 13.3252 2.59053 13.3252 4.70801C13.3249 6.89327 11.5525 8.66574 9.3672 8.66602C7.18159 8.66602 5.40947 6.89333 5.40919 4.70801L5.41408 4.50391C5.52024 2.413 7.24994 0.75 9.3672 0.75L9.57033 0.754883Z"
    />
    <path
      fill={fillWhen(filled)}
      className={filled ? undefined : "text-fg-secondary"}
      d="M9.36606 11.8252C13.1353 11.8252 16.373 14.0873 17.8075 17.3272V17.3281C18.3069 18.4578 17.6924 19.7734 16.4959 20.1504C14.6619 20.7283 12.2346 21.25 9.36606 21.25C6.49771 21.2499 4.07107 20.7283 2.23715 20.1504C1.04087 19.7734 0.425696 18.4592 0.924654 17.3281L0.92563 17.3272C2.36006 14.0875 5.5971 11.8255 9.36606 11.8252Z"
    />
  </svg>
)

export const NavSearchIcon = ({ size = 61, ...props }: IconProps) => (
  <svg {...frame(61, 61, props)} width={size} height={size}>
    <path d="M33.172 38.206C34.8987 37.475 36.3697 36.2481 37.3991 34.6808C38.4284 33.1135 38.9697 31.2763 38.9545 29.4012C38.9393 27.5262 38.3684 25.6976 37.3138 24.1472C36.2592 22.5968 34.7685 21.3941 33.0302 20.6912C31.2919 19.9883 29.3842 19.8166 27.5484 20.1983C25.7127 20.5799 24.0313 21.4977 22.7172 22.8352C21.4031 24.1727 20.5152 25.87 20.1659 27.7123C19.8166 29.5546 20.0216 31.459 20.7549 33.1848C21.7371 35.4962 23.5965 37.3231 25.9246 38.2646C28.2528 39.206 30.8594 39.185 33.172 38.206Z" />
    <path d="M36.1763 36.1768L40.9618 41.1439" />
  </svg>
)

export const MenuChevronIcon = (props: IconProps) => (
  <svg {...frame(7.07781, 11.7372, props)} strokeWidth={1} strokeLinecap="butt" strokeLinejoin="miter">
    <path d="M0.337862 0.368577L6.33786 5.86858L0.337862 11.3686" />
  </svg>
)

export const MenuArrowIcon = (props: IconProps) => (
  <svg {...frame(9, 9, props)} strokeWidth={1} strokeLinejoin="miter">
    <path d="M0.50001 8.5L8.5 0.5M0.5 0.5H8.5V8.5" />
  </svg>
)

export const HeaderSearchIcon = (props: IconProps) => (
  <svg {...frame(36, 36, props)}>
    <path d="M20.0722 23.6303C21.3154 23.083 22.3746 22.1645 23.1157 20.9911C23.8568 19.8177 24.2466 18.4422 24.2356 17.0384C24.2247 15.6346 23.8136 14.2656 23.0543 13.1049C22.295 11.9441 21.2217 11.0437 19.9701 10.5175C18.7186 9.99119 17.345 9.86268 16.0232 10.1484C14.7015 10.4341 13.4909 11.1212 12.5448 12.1226C11.5986 13.124 10.9593 14.3947 10.7078 15.7739C10.4563 17.1532 10.6039 18.579 11.1319 19.8711C11.8391 21.6015 13.1778 22.9693 14.8541 23.6741C16.5304 24.379 18.4071 24.3633 20.0722 23.6303Z" />
    <path d="M22.2354 22.1109L25.681 25.8297" />
  </svg>
)

export const HeaderBagIcon = (props: IconProps) => (
  <svg {...frame(19.0495, 19, props)} strokeWidth={1} strokeLinecap="butt" strokeLinejoin="miter">
    <path d="M14.2985 4.85048H15.3328C16.387 4.85048 17.2626 5.56835 17.3699 6.52042L18.4899 16.4491H18.489C18.5449 16.9445 18.3862 17.429 18.0418 17.8137C17.6513 18.2496 17.0723 18.5 16.4519 18.5H2.54737C1.92514 18.5 1.34439 18.2487 0.953921 17.81C0.610343 17.4227 0.453434 16.9363 0.512049 16.4392L1.69158 6.51051C1.8034 5.56385 2.67903 4.85048 3.72689 4.85048H4.73238M14.2985 4.85048C14.0712 2.41403 12.0134 0.5 9.51542 0.5C7.01749 0.5 4.95963 2.41403 4.73238 4.85048M14.2985 4.85048H4.73238" />
  </svg>
)

const disc = (props: IconProps) => ({
  width: 32,
  height: 32,
  viewBox: "0 0 32 32",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "currentColor",
  ...props,
})

export const FooterFacebookIcon = (props: IconProps) => (
  <svg {...disc(props)}>
    <g transform="translate(11.759 7.381)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.54858 15.8136V8.95799H7.89024L8.38573 6.03253L5.57859 6.02366V3.87301C5.57859 3.29833 5.89992 2.66794 6.90095 2.66794H8.48293V0.252589C8.48293 0.252589 6.78758 -0.178846 5.5041 0.0859899C4.22062 0.350826 2.52853 1.03261 2.52853 3.37817V5.99979H5.17178e-05V8.93659H2.51144V17.6189H5.51036L5.54858 15.8136Z"
      />
    </g>
  </svg>
)

export const FooterInstagramIcon = (props: IconProps) => (
  <svg {...disc(props)}>
    <g transform="translate(7.21 7.3)">
      <path d="M15.9674 5.14361C15.9673 3.20202 14.3906 1.62524 12.449 1.6252H5.1434C3.20197 1.62524 1.6252 3.202 1.62519 5.14361V12.2531C1.62519 14.1947 3.20196 15.7715 5.1434 15.7715H12.449C14.3906 15.7715 15.9674 14.1947 15.9674 12.2531V5.14361ZM17.5926 12.2531C17.5926 15.0923 15.2881 17.3967 12.449 17.3967H5.1434C2.30439 17.3967 0 15.0923 0 12.2531V5.14361C1.1052e-05 2.30449 2.3044 3.98301e-05 5.1434 0H12.449C15.2881 4.21584e-05 17.5925 2.30447 17.5926 5.14361V12.2531Z" />
    </g>
    <g transform="translate(11.78 11.77)">
      <path d="M6.93743 4.28065C6.93742 2.81328 5.74791 1.62376 4.28055 1.62376C2.8132 1.62377 1.62368 2.81329 1.62367 4.28065C1.62367 5.74801 2.81319 6.93752 4.28055 6.93753C5.74792 6.93753 6.93743 5.74803 6.93743 4.28065ZM8.5611 4.28065C8.5611 6.64474 6.64464 8.56121 4.28055 8.56121C1.91647 8.5612 0 6.64474 0 4.28065C1.27736e-05 1.91656 1.91648 9.46861e-05 4.28055 8.13802e-05C6.64464 8.13802e-05 8.56109 1.91656 8.5611 4.28065Z" />
    </g>
    <g transform="translate(19.64 10.58)">
      <path d="M0.96052 1.92112C1.491 1.92112 1.92104 1.49108 1.92104 0.9606C1.92104 0.430119 1.491 8.13802e-05 0.96052 8.13802e-05C0.430043 8.13802e-05 0 0.430119 0 0.9606C0 1.49108 0.430043 1.92112 0.96052 1.92112Z" />
    </g>
  </svg>
)

export const FooterTikTokIcon = (props: IconProps) => (
  <svg {...disc(props)}>
    <g transform="translate(7.98 6.71)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.1532 14.2402C10.5577 16.7248 8.32027 18.5738 5.65425 18.5738C2.53364 18.5738 0 16.0402 0 12.9193C0 9.79871 2.53364 7.26507 5.65425 7.26507H6.09634V10.0149H5.65425C4.05128 10.0149 2.7498 11.3164 2.7498 12.9193C2.7498 14.5223 4.05128 15.8238 5.65425 15.8238C6.90495 15.8238 7.9719 15.0318 8.38061 13.9223L8.41908 0.0187477L11.3606 0C11.3606 0 11.609 3.94526 16.0049 4.29187L16.0397 7.59488C16.0397 7.59488 13.1663 8.1516 11.2794 5.51837C11.2794 5.51837 11.2386 12.5472 11.2232 13.9027C11.222 14.0175 11.1532 14.2402 11.1532 14.2402Z"
      />
    </g>
  </svg>
)

export const FooterYouTubeIcon = (props: IconProps) => (
  <svg {...disc(props)}>
    <g transform="translate(6.97 9.71)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.0595 3.78073V8.79679C18.0595 10.8836 16.3654 12.5777 14.2786 12.5777H3.78069C1.6941 12.5777 0 10.8836 0 8.79679V3.78073C0 1.69415 1.6941 8.13802e-05 3.78069 8.13802e-05H14.2786C16.3654 8.13802e-05 18.0595 1.69415 18.0595 3.78073ZM11.8048 6.01518L7.35353 3.78954C7.24543 3.73547 7.11715 3.74126 7.01438 3.80462C6.91162 3.86824 6.84898 3.98039 6.84898 4.10134V8.55263C6.84898 8.67333 6.91162 8.78573 7.01438 8.8491C7.11715 8.91272 7.24543 8.9185 7.35353 8.86444L11.8048 6.63879C11.9229 6.5797 11.9976 6.459 11.9976 6.32699C11.9976 6.19497 11.9229 6.07427 11.8048 6.01518Z"
      />
    </g>
  </svg>
)

export const FooterLinkedInIcon = (props: IconProps) => (
  <svg {...disc(props)}>
    <g transform="translate(8.4 8.47)">
      <path d="M1.90331 3.80663C2.95447 3.80663 3.80661 2.95449 3.80661 1.90332C3.80661 0.852144 2.95447 0 1.90331 0C0.852138 0 0 0.852144 0 1.90332C0 2.95449 0.852138 3.80663 1.90331 3.80663Z" />
    </g>
    <g transform="translate(8.68 13.49)">
      <path d="M3.26573 0H0V10.0353H3.26573V0Z" />
    </g>
    <g transform="translate(13.73 13.1)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.29816 4.34196V10.4317H8.13802e-05V0.3964H3.29816V1.56941C3.63697 -0.305873 9.99053 -1.70171 9.86266 4.75003C9.86266 4.75003 9.85862 4.74811 9.85862 4.7496V10.4317H6.4418V4.43224C6.4418 3.01091 3.90526 2.22346 3.29816 4.34196Z"
      />
    </g>
  </svg>
)
